import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { StudyRecordWithRestrictedAttributes } from "../../interfaces/StudyRecordInterface";
import { paths } from "../../lib/api/v1";

// タイムライン用の学習記録取得（boron PR #6820 / WEB-1817）。
//   GET /api/v1/sections/{section_id}/timeline/study_records
//     student_id 未指定 → 校舎全体（リアルタイムタブ）
//     student_id 指定   → 当該生徒に絞り込み（生徒タイムライン）
// 既存 /timeline と同一の since/till カーソル・レスポンス構造。校舎リアルタイムタブと
// 生徒タイムラインの双方から再利用する。
const URL = "/api/v1/sections/{section_id}/timeline/study_records";
type Url = typeof URL;

type TimelineStudyRecordsResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];

type Query = paths[Url]["get"]["parameters"]["query"];

type Props = {
  sectionId: string;
  // 生徒タイムラインで指定。未指定で校舎全体（リアルタイム）。
  studentId?: string;
  // StudentFilter のクエリ（job/status/tag_ids/smart_tag）。URL クエリをそのまま渡す。
  queryParams: Query;
  enabled: boolean;
};

export const useFetchTimelineStudyRecords = ({
  sectionId,
  studentId,
  queryParams,
  enabled,
}: Props) => {
  const queryKey = [URL, sectionId, studentId, queryParams];

  const { data, ...result } = useInfiniteQuery<
    TimelineStudyRecordsResponse,
    HTTPErrors
  >({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchTimelineStudyRecords({
        sectionId,
        query: {
          ...queryParams,
          student_id: studentId,
          since: pageParam as string | undefined,
        },
      }),
    initialPageParam: null,
    getNextPageParam: ({ studyRecords }) => studyRecords.meta.till,
    enabled,
  });

  const queryClient = useQueryClient();

  const updateStudyRecordQueryCache = (
    newStudyRecord: StudyRecordWithRestrictedAttributes,
  ) => {
    queryClient.setQueriesData<InfiniteData<TimelineStudyRecordsResponse>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return {
            pages: [],
            pageParams: [],
          } as InfiniteData<TimelineStudyRecordsResponse>;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            studyRecords: {
              data: page.studyRecords.data.map((oldStudyRecord) =>
                oldStudyRecord.id === newStudyRecord.id
                  ? newStudyRecord
                  : oldStudyRecord,
              ),
              meta: page.studyRecords.meta,
            },
          })),
        } as InfiniteData<TimelineStudyRecordsResponse>;
      },
    );
  };

  return {
    data: data?.pages.flatMap((page) => page.studyRecords.data),
    meta: data?.pages.flatMap((page) => page.studyRecords.meta)[0],
    updateStudyRecordQueryCache,
    ...result,
  };
};

const fetchTimelineStudyRecords = async ({
  sectionId,
  query,
}: {
  sectionId: string;
  query: Query;
}) => {
  const { data, response } = await boronClient.GET(URL, {
    params: {
      path: { section_id: sectionId },
      query,
    },
  });

  if (response.ok && data) {
    return data;
  }

  throw await createError(response);
};
